import { Attribute, Directive, forwardRef, Input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

import { VALIDATE_PATTERN_ERROR_PREFIX, validatePatternConfiguration } from '@df/ui/form/validate-pattern.config';

/**
 * Pattern like validator directive
 * You can define pattern in config
 *
 *  email: {
 *       pattern: /^[a-zA-Z0-9.!#$%&'*+-\/=?^_`{|}~]+@[a-zA-Z0-9-]+\.[.a-zA-Z0-9]+$/,
 *       errorMessage: 'Please enter valid email'
 * }
 *
 * and use in template on input using pattern id:
 *
 *      <input class="input"
 *             type="text"
 *             name="lastname"
 *             [(ngModel)]="data.lastname"
 *             validatePattern="email"
 *             [muiInput]="'Last name'"
 *             maxlength="50"
 *             required
 *             lastname
 *             #lastnameInput>
 *
 */
@Directive({
    selector: '[validatePattern]',
    standalone: true,
    providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => ValidatePatternDirective), multi: true }]
})
export class ValidatePatternDirective implements Validator {
    @Input({
        required: true
    })
    public validatePattern!: string;

    /**
     * Returns pattern from config for used validate pattern id
     * @returns {RegExp | undefined}
     */
    get pattern(): RegExp | undefined {
        if (this.validatePattern) {
            const patternConfig = validatePatternConfiguration[this.validatePattern];
            if (patternConfig) {
                const pattern = patternConfig.pattern;
                if (pattern instanceof RegExp) {
                    return pattern;
                } else {
                    return new RegExp(pattern);
                }
            }
        }
        return undefined;
    }

    /**
     * Validation error to set on input
     * @returns {string}
     */
    get errorName(): string {
        return VALIDATE_PATTERN_ERROR_PREFIX + this.validatePattern;
    }

    /**
     * Validation method
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    validate(control: AbstractControl): ValidationErrors | null {
        const value: string = control.value?.toString();
        if (value && this.pattern && !value.match(this.pattern)) {
            return {
                [this.errorName]: false
            };
        }
        return null;
    }
}
